<template>
  <div id="page-product-form">
    <vs-row>
      <vs-col :class="readonly ? 'readonly-form' : ''">
        <card-page
            :card-title="cardTitle"
            :switch-model="model.IsEnabled"
        >
          <vue-element-loading :active="busy"></vue-element-loading>
          <vue-form-generator
              ref="form"
              :model="model"
              :options="formOptions"
              :schema="schema"
          />
        </card-page>
      </vs-col>
    </vs-row>
  </div>
</template>

<script>

import FormModelMixin from '@/components/mixins/FormModelMixin';
import CardPage from '../../components/Views/CardPage';

const selectOptions = {
  showLabels: false,
  key: 'id',
  trackBy: 'id',
  label: 'name',
};

export default {
  name: 'ProductForm',
  components: {
    CardPage,
  },
  props: {
    value: {
      type: Object,
      required: true
    },
    cardTitle: {
      type: String,
      required: false,
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    }
  },
  mixins: [FormModelMixin],
  data() {
    return {
      selectedReferences: [],
      model: {
        ProductCode: '',
        ProductDescription: '',
        LIORiskCode_FK: null,
        LloydsRiskCode_FK: null,
        LloydsRiskCodeILPercentage: '',
        InsuranceType_FK: null,
        Keywords: '',
        Wording_FK: null,
        FrontJacket_FK: null,
        RearJacket_FK: null,
        StandardClause_FK: null,
      },
      schema: {
        groups: [
          {
            legend: 'General',
            styleClasses: 'w-full',
            fields: [
              {
                disabled: this.readonly,
                type: 'input',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'Product Code',
                model: 'ProductCode',
                required: !this.readonly,
                validator: ['string', 'required'],
              },
              {
                disabled: this.readonly,
                type: 'input',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'Product Description',
                model: 'ProductDescription',
                required: !this.readonly,
                validator: 'required',
              },
              {
                disabled: this.readonly,
                type: 'vueMultiSelect',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'Insurance Type',
                model: 'InsuranceType_FK',
                required: !this.readonly,
                validator: 'selectRequired',
                values: () => this.insuranceTypes,
                selectOptions,
              },
              {
                disabled: this.readonly,
                type: 'vueMultiSelect',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'LIO Risk Code',
                model: 'LIORiskCode_FK',
                values: () => this.lioRiskCodes,
                selectOptions,
                required: !this.readonly,
                validator: 'selectRequired',
              },
              {
                disabled: this.readonly,
                type: 'vueMultiSelect',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'Lloyds Risk Code',
                model: 'LloydsRiskCode_FK',
                values: () => this.lloydsRiskCodes,
                selectOptions,
                required: !this.readonly,
                validator: 'selectRequired',
              },
              {
                disabled: this.readonly,
                type: 'input',
                inputType: 'number',
                styleClasses: 'w-1/3',
                label: 'Lloyds Risk Code IL %',
                model: 'LloydsRiskCodeILPercentage',
                required: !this.readonly,
                validator: ['positive', 'number'],
              },
            ]
          },
          {
            legend: 'Documents',
            styleClasses: 'w-full',
            fields: [
              {
                disabled: this.readonly,
                type: 'vueMultiSelect',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'Wording',
                model: 'Wording_FK',
                values: () => this.wordings,
                selectOptions,
                required: !this.readonly,
                validator: 'selectRequired',
              },
              {
                disabled: this.readonly,
                type: 'vueMultiSelect',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'Front Jacket',
                model: 'FrontJacket_FK',
                values: () => this.frontJackets,
                selectOptions,
              },
              {
                disabled: this.readonly,
                type: 'vueMultiSelect',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'Standard Clause',
                model: 'StandardClause_FK',
                values: () => this.standardClauses,
                selectOptions,
              },
              {
                disabled: this.readonly,
                type: 'vueMultiSelect',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'Rear Jacket',
                model: 'RearJacket_FK',
                values: () => this.rearJackets,
                selectOptions
              },
            ]
          },
          {
            legend: 'Additional',
            styleClasses: 'w-full',
            fields: [
              {
                disabled: this.readonly,
                type: 'input',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'Keywords',
                model: 'Keywords',
              },
            ]
          },
        ],
      },
      formOptions: {
        validateAfterLoad: false,
        validateAfterChanged: true,
      },
      wordings: [],
      frontJackets: [],
      standardClauses: [],
      rearJackets: [],
      lioRiskCodes: [],
      lloydsRiskCodes: [],
      insuranceTypes: [],
      formReferences: [
        'form',
      ],
      options: [
        {
          endpoint: () => this.$api.productLIORiskCode.list(),
          optionsFieldName: 'lioRiskCodes',
          nameFieldName: 'LIORiskCode',
          idFieldName: 'LIORiskCode_FK',
        },
        {
          endpoint: () => this.$api.productLloydsRiskCode.list(),
          optionsFieldName: 'lloydsRiskCodes',
          nameFieldName: 'LloydsRiskCode',
          idFieldName: 'LloydsRiskCode_FK',
        },
        {
          endpoint: () => this.$api.productInsuranceType.list(),
          optionsFieldName: 'insuranceTypes',
          nameFieldName: 'Type',
          idFieldName: 'InsuranceType_FK',
        },
        {
          endpoint: () => this.$api.productWording.list(),
          optionsFieldName: 'wordings',
          nameFieldName: 'WordingCode',
          idFieldName: 'Wording_FK',
        },
        {
          endpoint: () => this.$api.productFrontJacket.list(),
          optionsFieldName: 'frontJackets',
          nameFieldName: 'FrontJacket',
          idFieldName: 'FrontJacket_FK',
        },
        {
          endpoint: () => this.$api.productRearJacket.list(),
          optionsFieldName: 'rearJackets',
          nameFieldName: 'RearJacket',
          idFieldName: 'RearJacket_FK',
        },
        {
          endpoint: () => this.$api.productStandardClause.list(),
          optionsFieldName: 'standardClauses',
          nameFieldName: 'StandardClause',
          idFieldName: 'StandardClause_FK',
        }
      ]
    };
  },
};
</script>
